import { queryKeys } from '@mevaser/shared'
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  EmptyState,
  humanizeStatus,
  REPAIR_STATUS_TONE,
  Skeleton,
} from '@mevaser/ui'
import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { CalendarDays, CalendarOff, Wrench } from 'lucide-react'
import { useMemo, useState } from 'react'
import { Page, PageHeader } from '../components/app-shell.js'
import {
  firstOfMonth,
  fromISODate,
  gridDays,
  MonthCalendar,
  type MonthCalendarEntry,
  MonthNav,
  toISODate,
} from '../components/month-calendar.js'
import { api } from '../lib/api.js'

interface RepairListItem {
  id: string
  ticketNumber: string
  status: string
  customerName: string | null
  deviceDescription: string
  dropOffDate: string | null
  promisedDate: string | null
  workDate: string | null
  assignedStaffName: string | null
  createdAt: string
}

type ViewMode = 'dropoff' | 'promised' | 'work' | 'both'

/**
 * The workshop's calendar: every repair ticket with a drop-off date, a
 * promised date, a work date, or any combination, laid out by day. A ticket
 * can show up to three times — once for when the customer is bringing it in,
 * once for when it is due back to them, once for when a technician is booked
 * on it — because those are three different questions and a ticket can
 * answer any mix of them.
 */
export function RepairsPage() {
  const [monthStart, setMonthStart] = useState(() => firstOfMonth(new Date()))
  const [view, setView] = useState<ViewMode>('both')

  const days = useMemo(() => gridDays(monthStart), [monthStart])
  const from = days[0] ? toISODate(days[0]) : undefined
  const to = days[41] ? toISODate(days[41]) : undefined

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.repairs.list({ from, to, dateField: view === 'both' ? 'either' : view }),
    queryFn: () =>
      api.get<{ items: RepairListItem[] }>('/repairs', {
        query: { from, to, dateField: view === 'both' ? 'either' : view, pageSize: 500 },
      }),
  })

  const tickets = data?.items ?? []

  // Not tied to the month on screen: a ticket with no date at all would
  // otherwise never appear on this page.
  const { data: undatedData } = useQuery({
    queryKey: queryKeys.repairs.list({ undated: true, open: true }),
    queryFn: () =>
      api.get<{ items: RepairListItem[] }>('/repairs', {
        query: { undated: true, open: true, pageSize: 500 },
      }),
  })
  const undated = undatedData?.items ?? []

  const entries = useMemo(() => {
    const out: MonthCalendarEntry[] = []
    const add = (ticket: RepairListItem, kind: 'dropoff' | 'promised' | 'work', date: string) =>
      out.push({
        key: `${ticket.id}-${kind}`,
        date,
        render: () => (
          <Link
            to="/repairs/$ticketId"
            params={{ ticketId: ticket.id }}
            className={`block truncate rounded px-1.5 py-0.5 text-2xs ${
              kind === 'dropoff'
                ? 'bg-success-subtle text-success'
                : kind === 'promised'
                  ? 'bg-info-subtle text-info'
                  : 'bg-warning-subtle text-warning'
            }`}
            title={`${ticket.ticketNumber} — ${ticket.customerName ?? 'Customer'}`}
          >
            {ticket.ticketNumber}
            {kind === 'work' && ticket.assignedStaffName
              ? ` · ${ticket.assignedStaffName}`
              : ticket.customerName
                ? ` · ${ticket.customerName}`
                : ''}
          </Link>
        ),
      })
    for (const ticket of tickets) {
      if (ticket.dropOffDate && (view === 'both' || view === 'dropoff')) {
        add(ticket, 'dropoff', ticket.dropOffDate)
      }
      if (ticket.promisedDate && (view === 'both' || view === 'promised')) {
        add(ticket, 'promised', ticket.promisedDate)
      }
      if (ticket.workDate && (view === 'both' || view === 'work')) {
        add(ticket, 'work', ticket.workDate)
      }
    }
    return out
  }, [tickets, view])

  const needsBooking = tickets.filter(
    (t) => !t.workDate && t.status !== 'completed' && t.status !== 'cancelled',
  )

  return (
    <Page>
      <PageHeader
        title="Repairs"
        description="Scheduled repairs — when they're due back, and when a technician is booked on them."
      />

      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <MonthNav monthStart={monthStart} onChange={setMonthStart} />

        <div className="flex items-center gap-1 rounded-md border border-border p-0.5">
          {(
            [
              { key: 'both', label: 'All' },
              { key: 'dropoff', label: 'Drop-offs' },
              { key: 'promised', label: 'Pickups' },
              { key: 'work', label: 'Workshop' },
            ] as const
          ).map((opt) => (
            <Button
              key={opt.key}
              variant={view === opt.key ? 'primary' : 'ghost'}
              size="sm"
              onClick={() => setView(opt.key)}
            >
              {opt.label}
            </Button>
          ))}
        </div>
      </div>

      <div className="mb-4 flex flex-wrap gap-4 text-xs text-foreground-muted">
        {view === 'both' || view === 'dropoff' ? (
          <span className="flex items-center gap-1.5">
            <span className="size-2 rounded-full bg-success" aria-hidden="true" /> Customer drop-off
          </span>
        ) : null}
        {view === 'both' || view === 'promised' ? (
          <span className="flex items-center gap-1.5">
            <span className="size-2 rounded-full bg-info" aria-hidden="true" /> Due back to customer
          </span>
        ) : null}
        {view === 'both' || view === 'work' ? (
          <span className="flex items-center gap-1.5">
            <span className="size-2 rounded-full bg-warning" aria-hidden="true" /> Technician booked
          </span>
        ) : null}
      </div>

      {isLoading ? (
        <Skeleton className="h-[36rem] w-full" />
      ) : (
        <MonthCalendar monthStart={monthStart} entries={entries} />
      )}

      {undated.length > 0 ? (
        <Card className="mt-4">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CalendarOff className="size-4" aria-hidden="true" /> Undated
              <Badge tone="warning">{undated.length}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <p className="px-4 pb-3 text-xs text-foreground-muted">
              Open tickets with no drop-off, pickup or workshop date, so they can't appear on the
              calendar. Give each one a date.
            </p>
            <table className="w-full text-sm">
              <thead className="border-b border-border text-2xs uppercase tracking-wide text-foreground-subtle">
                <tr>
                  <th className="px-4 py-2 text-start font-medium">Ticket</th>
                  <th className="px-4 py-2 text-start font-medium">Customer</th>
                  <th className="px-4 py-2 text-start font-medium">Opened</th>
                  <th className="px-4 py-2 text-start font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {undated.map((ticket) => (
                  <tr key={ticket.id} className="border-b border-border last:border-0">
                    <td className="px-4 py-2.5">
                      <Link
                        to="/repairs/$ticketId"
                        params={{ ticketId: ticket.id }}
                        className="font-medium hover:underline"
                      >
                        {ticket.ticketNumber}
                      </Link>
                      <p className="text-xs text-foreground-muted">{ticket.deviceDescription}</p>
                    </td>
                    <td className="px-4 py-2.5">{ticket.customerName ?? '—'}</td>
                    <td className="px-4 py-2.5">
                      {new Date(ticket.createdAt).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                      })}
                    </td>
                    <td className="px-4 py-2.5">
                      <Badge tone={REPAIR_STATUS_TONE[ticket.status] ?? 'neutral'}>
                        {humanizeStatus(ticket.status)}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      ) : null}

      <Card className="mt-4">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Wrench className="size-4" aria-hidden="true" /> Needs booking
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {needsBooking.length === 0 ? (
            <EmptyState
              icon={CalendarDays}
              title="Nothing waiting"
              description="Every open ticket in this range has a technician booked."
            />
          ) : (
            <table className="w-full text-sm">
              <thead className="border-b border-border text-2xs uppercase tracking-wide text-foreground-subtle">
                <tr>
                  <th className="px-4 py-2 text-start font-medium">Ticket</th>
                  <th className="px-4 py-2 text-start font-medium">Customer</th>
                  <th className="px-4 py-2 text-start font-medium">Promised</th>
                  <th className="px-4 py-2 text-start font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {needsBooking.map((ticket) => (
                  <tr key={ticket.id} className="border-b border-border last:border-0">
                    <td className="px-4 py-2.5">
                      <Link
                        to="/repairs/$ticketId"
                        params={{ ticketId: ticket.id }}
                        className="font-medium hover:underline"
                      >
                        {ticket.ticketNumber}
                      </Link>
                      <p className="text-xs text-foreground-muted">{ticket.deviceDescription}</p>
                    </td>
                    <td className="px-4 py-2.5">{ticket.customerName ?? '—'}</td>
                    <td className="px-4 py-2.5">
                      {ticket.promisedDate
                        ? fromISODate(ticket.promisedDate).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                          })
                        : '—'}
                    </td>
                    <td className="px-4 py-2.5">
                      <Badge tone={REPAIR_STATUS_TONE[ticket.status] ?? 'neutral'}>
                        {humanizeStatus(ticket.status)}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </Page>
  )
}
